import { getCurrentOrg } from '../../shared/organization/organizationActions';
import { useOrganizationStore } from '../../shared/organization/organizationStore';
import { FirestoreService } from '../../shared/services/firestoreService';
import { addStatus } from '../../shared/status/statusActions';
import { useCategoriesStore } from './categories.store';
import { Category } from './types';
import { getDiagramStore } from '@diagram/store/diagram.store';

export const createCategory = async (data: Partial<Category>) => {
  const FirestoreService = await import(
    '../../shared/services/firestoreService'
  ).then((mod) => mod.FirestoreService);
  const org = useOrganizationStore.getState().organization;
  const { activeDrilldown, drilldown, drilldowns } =
    getDiagramStore().getState();
  if (!org) {
    addStatus({
      type: 'error',
      message: 'No organization selected',
      autoDismiss: true,
    });
    return;
  }

  const firestore = new FirestoreService<Category>(
    `/organizations/${org.id}/categories`
  );
  try {
    const result = await firestore.create(data as Category);
    addStatus({
      type: 'success',
      message: 'Category created successfully',
      autoDismiss: true,
    });
    if (activeDrilldown) {
      drilldown.patchDrilldown({
        ...drilldowns[activeDrilldown],
        categoryId: result,
      });
    }
    useCategoriesStore.getState().addItem({ ...data, id: result } as Category);
  } catch (error) {
    console.error('createCategory Error:', error);
    addStatus({
      type: 'error',
      message: 'Error creating category',
      autoDismiss: true,
    });
  }
};

export const updateCategory = async (category: Category) => {
  const FirestoreService = await import(
    '../../shared/services/firestoreService'
  ).then((mod) => mod.FirestoreService);
  const { updateItem } = useCategoriesStore.getState();
  const { activeDrilldown, drilldown, drilldowns } =
    getDiagramStore().getState();
  if (drilldowns[activeDrilldown].categoryId === category.id) {
    drilldown.patchDrilldown({
      ...drilldowns[activeDrilldown],
      categoryId: category.id,
    });
  }
  try {
    const org = getCurrentOrg();
    const firestore = new FirestoreService<Category>(
      `/organizations/${org.id}/categories`
    );
    updateItem(category);
    await firestore.update(category.id, category);
    addStatus({
      type: 'success',
      message: 'Category updated successfully',
      autoDismiss: true,
    });
  } catch (error) {
    console.error('Error updating category:', error);
  }
};

export const deleteCategory = async (id: string) => {
  const { deleteItem } = useCategoriesStore.getState();
  try {
    const org = getCurrentOrg();
    const firestore = new FirestoreService<Category>(
      `/organizations/${org.id}/categories`
    );
    const result = await firestore.delete(id);
    deleteItem(id);

    addStatus({
      type: 'success',
      message: 'Category deleted successfully',
      autoDismiss: true,
    });
    return result;
  } catch (error) {
    console.error('deleteCategory Error:', error);
    addStatus({
      type: 'error',
      message: 'Error deleting category',
      autoDismiss: true,
    });
  }
};

export const fetchCategories = async () => {
  const FirestoreService = await import(
    '../../shared/services/firestoreService'
  ).then((mod) => mod.FirestoreService);
  const { setItems } = useCategoriesStore.getState();
  const org = getCurrentOrg();

  try {
    const firestore = new FirestoreService<Category>(
      `/organizations/${org.id}/categories`
    );
    const categories = await firestore.getAll();
    setItems(categories);
  } catch (error) {
    addStatus({
      type: 'error',
      message: 'Error fetching categories',
      autoDismiss: true,
    });
    console.error('Error fetching categories:', error);
  }
};
